import { Link } from "@tanstack/react-router";
import { ChevronDown, CircleCheck, PlugZap, TriangleAlert } from "lucide-react";

import { useCollapsed } from "@/hooks/use-collapsed";
import { pageOriginSearch } from "@/lib/detail-origin";
import { monthLongLabel } from "@/lib/persons/project-guidance";
import type { TeamAttentionEntry, TeamFlag } from "@/lib/persons/team-attention";
import type { TeamScope } from "@/lib/persons/team-summary";
import { cn } from "@/lib/utils";

/**
 * Remembered per browser, not per scope: the block answers the same question in
 * both, and someone who folds it away on My team has said what they want from
 * it on the company view too.
 */
const COLLAPSE_KEY = "team-attention-collapsed";

/** `322.5` → `"322.5"`, `96` → `"96"`. Matches the table below. */
function hoursValue(hours: number): string {
  return String(hours % 1 === 0 ? hours : hours.toFixed(1));
}

/** `"2026-11-03"`, `"2026-11-07"` → `"3–7 Nov"`; a single day → `"3 Nov"`. */
function rangeLabel(startDate: string, endDate: string): string {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${startDate} – ${endDate}`;
  }
  const month = (d: Date) => d.toLocaleDateString("en-US", { month: "short" });
  if (startDate === endDate) return `${start.getDate()} ${month(start)}`;
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()}–${end.getDate()} ${month(end)}`;
  }
  return `${start.getDate()} ${month(start)} – ${end.getDate()} ${month(end)}`;
}

/**
 * One flag as a sentence a manager can act on, carrying the real figures — the
 * gate that raised it is deliberately not restated, so the reader judges the
 * numbers rather than trusting a threshold.
 */
function flagText(flag: TeamFlag): string {
  switch (flag.kind) {
    case "underBooked": {
      const when = monthLongLabel(flag.month);
      if (flag.guidedHours === null) return `Nothing booked for ${when}`;
      if (flag.capacityHours === null || flag.capacityHours === 0) {
        return `Only ${hoursValue(flag.guidedHours)} hours booked for ${when}`;
      }
      return `Only ${hoursValue(flag.guidedHours)} of ${hoursValue(
        flag.capacityHours,
      )} hours booked for ${when}`;
    }
    case "overCommitted": {
      const when = monthLongLabel(flag.month);
      const guided = flag.guidedHours === null ? "?" : hoursValue(flag.guidedHours);
      if (flag.capacityHours === null) return `Booked beyond capacity in ${when}`;
      return `Booked ${guided} hours against ${hoursValue(
        flag.capacityHours,
      )} of capacity in ${when}`;
    }
    case "notLogged": {
      const when = monthLongLabel(flag.month);
      if (flag.none) return `Logged no time in ${when}`;
      return `Logged only ${hoursValue(flag.loggedHours)} of ${hoursValue(
        flag.capacityHours,
      )} hours in ${when}`;
    }
    case "leavePending": {
      const shown = flag.requests.slice(0, 2).map((r) => rangeLabel(r.startDate, r.endDate));
      const extra = flag.requests.length - shown.length;
      const when = extra > 0 ? `${shown.join(", ")} +${extra} more` : shown.join(", ");
      return flag.yours
        ? `Time off awaiting your approval (${when})`
        : `Time off awaiting approval (${when})`;
    }
  }
}

/**
 * The "Needs your attention" block above the team table.
 *
 * Renders one row per PERSON, not per problem: a manager acts on a person, so
 * two things wrong with the same report is one row with two sentences.
 */
/**
 * How to refer to the people the block is about.
 *
 * Company scope covers colleagues the viewer does not manage, so calling them
 * "reports" there is simply false — and it appears in the one sentence that has
 * to be precise, the count of people NOS holds no figures for (AC-915).
 * Exported and pure so the wording is pinned by a test rather than by reading
 * it on screen in the one scope that happens to be open.
 */
export function attentionSubject(scope: TeamScope, count: number): string {
  if (scope === "company") return count === 1 ? "person" : "people";
  return count === 1 ? "report" : "reports";
}

/**
 * "Every report" / "Everyone" / "Everyone shown here", for the all-clear
 * sentence.
 *
 * The third case matters: under a filter the block has only been told about the
 * people on screen, so "Everyone is booked and up to date" would be a claim
 * about the company that nothing here checked.
 */
export function attentionAllClearSubject(scope: TeamScope, narrowed = false): string {
  if (narrowed) return "Everyone shown here";
  return scope === "company" ? "Everyone" : "Every report";
}

export function TeamAttention({
  attention,
  leaveKnown,
  unassessed,
  isSelf,
  scope,
  narrowed = false,
}: {
  attention: TeamAttentionEntry[] | null;
  leaveKnown: boolean;
  /** People NOS holds no figures for, so nothing about them can be claimed. */
  unassessed: number;
  isSelf: boolean;
  /** Which roster this is about — decides whether they are "reports". */
  scope: TeamScope;
  /**
   * Whether a filter is narrowing the roster, so the all-clear can say it is
   * about the people shown rather than about everyone.
   */
  narrowed?: boolean;
}) {
  // Above the two early returns below, which are the "nothing to fold" states:
  // a hook cannot be called conditionally, and both of those render a single
  // line that has nothing to collapse.
  const [collapsed, toggleCollapsed] = useCollapsed(COLLAPSE_KEY);

  // Null means the figures could not be read. Saying nothing needs attention on
  // the strength of a failed read is the one thing this block must never do.
  if (attention === null) {
    return (
      <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
        <PlugZap className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Can't check right now.</span> Booking,
          logged time and pending leave are read from NOS, which this environment can't reach — so
          nothing can be flagged either way.
        </p>
      </div>
    );
  }

  if (attention.length === 0) {
    // With nobody assessable there is no all-clear to give — every report is
    // simply unknown to NOS, and saying they are all fine would be a guess.
    const allClear = unassessed === 0;
    return (
      <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
        {allClear ? (
          <CircleCheck className="mt-0.5 size-4 shrink-0 text-success-foreground" />
        ) : (
          <PlugZap className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        )}
        <p className="text-muted-foreground">
          {allClear ? (
            <>
              <span className="font-medium text-foreground">Nothing needs your attention.</span>{" "}
              {attentionAllClearSubject(scope, narrowed)} is booked, up to date on their time, and
              has no leave waiting on a decision.
            </>
          ) : (
            <>
              <span className="font-medium text-foreground">Nothing to flag.</span>{" "}
              {`${unassessed === 1 ? "One" : unassessed} ${attentionSubject(scope, unassessed)} ${
                unassessed === 1 ? "has" : "have"
              } no figures in NOS, so nothing about their booking or time can be checked.`}
            </>
          )}
          {!leaveKnown && " Pending leave couldn't be checked."}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border">
      {/* The header is the toggle, and it keeps the COUNT when folded — that is
          what makes collapsing safe. A manager who has folded it still sees
          whether anyone needs them, and opens it when the number is not zero.
          Same affordance as the Filters panel on the directory pages: icon,
          count, chevron that rotates. */}
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={toggleCollapsed}
        className={cn(
          "flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/30",
          !collapsed && "border-b",
        )}
      >
        <TriangleAlert className="size-4 shrink-0 text-warning-foreground" />
        <h2 className="flex-1 font-semibold text-sm">
          Needs your attention{" "}
          <span className="font-normal text-muted-foreground">({attention.length})</span>
        </h2>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            !collapsed && "rotate-180",
          )}
        />
      </button>

      {collapsed ? null : (
        <>
          <ul className="divide-y">
            {attention.map((entry) => (
              <li key={entry.personId}>
                <Link
                  to="/dashboard/persons/$personId"
                  params={{ personId: entry.personId }}
                  // Same rule as the table: only the viewer's own team page has a
                  // stable address a breadcrumb can point back at.
                  search={isSelf ? pageOriginSearch("team") : undefined}
                  className="flex items-baseline gap-2 px-4 py-2 text-sm hover:bg-muted/30"
                >
                  {/* One line per person, no avatar and no job title. Both are
                  repeated in the table immediately below, and stacked they made
                  this block 530px — over half the first screen, which pushed
                  the team itself out of view. Nobody is hidden to achieve it. */}
                  <span className="shrink-0 font-medium">{entry.name}</span>
                  <span className="min-w-0 text-muted-foreground">
                    {entry.flags.map((flag) => flagText(flag)).join(" · ")}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {(!leaveKnown || unassessed > 0) && (
            <p className="border-t px-4 py-2 text-muted-foreground text-xs">
              {!leaveKnown &&
                "Pending time off couldn't be checked, so leave isn't included above. "}
              {unassessed > 0 &&
                `${unassessed} ${unassessed === 1 ? "report has" : "reports have"} no figures in NOS and could not be checked.`}
            </p>
          )}
        </>
      )}
    </div>
  );
}
